import { useState } from 'react';
import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { Snackbar } from '@liminal/web-ui/feedback/Snackbar';
import { useSnackbar } from '@liminal/web-ui/feedback/useSnackbar';
import { Button } from '@liminal/web-ui/primitives/Button';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { useDisableFFlag, useEnableFFlag, useFFlags } from '@/hooks/useFFlags';
import type { FFlag } from '@/api/types';
import { fontWeights } from '@liminal/brand/scale';
import { FONT_STACK_BODY } from '@liminal/brand/font-faces';
import { getErrorMessage } from '@/errors/messages';

const COLUMN_COUNT = 4;

/** Every flag comes back in one response, so this sorts the whole list. */
const SORT_ACCESSORS = {
  name: (flag: FFlag) => flag.name,
  enabled: (flag: FFlag) => flag.enabled ?? false,
  public: (flag: FFlag) => flag.public ?? false,
};

export function FFlagsListPage() {
  const { data, isLoading, isError } = useFFlags();
  const enableMutation = useEnableFFlag();
  const disableMutation = useDisableFFlag();
  const snackbar = useSnackbar();
  const [pendingName, setPendingName] = useState<string | null>(null);

  const { rows: flags, sort } = useTableSort(data?.fflags ?? [], SORT_ACCESSORS, 'name', 'asc');

  const handleToggle = async (flag: FFlag) => {
    setPendingName(flag.name);
    try {
      if (flag.enabled) {
        await disableMutation.mutateAsync(flag.name);
        snackbar.show(`Disabled ${flag.name}`);
      } else {
        await enableMutation.mutateAsync(flag.name);
        snackbar.show(`Enabled ${flag.name}`);
      }
    } catch (e) {
      snackbar.show(getErrorMessage(e), 'error');
    } finally {
      setPendingName(null);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Toggle the global state for each flag. Per-user overrides always win — manage those in the Users tab.
      </Typography>

      <Card variant="elevated" padding="none">
        <TableContainer>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <SortableTableCell column="name" sort={sort}>
                  Name
                </SortableTableCell>
                <SortableTableCell column="enabled" sort={sort}>
                  Enabled
                </SortableTableCell>
                <SortableTableCell column="public" sort={sort}>
                  Public
                </SortableTableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={COLUMN_COUNT}
              isLoading={isLoading}
              isError={isError}
              isEmpty={flags.length === 0}
              emptyMessage="No feature flags found."
              errorMessage="Could not load feature flags."
            >
              {flags.map((flag) => (
                <TableRow key={flag.name} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: FONT_STACK_BODY, fontWeight: fontWeights.bold }}>
                      {flag.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusPill
                      label={flag.enabled ? 'Enabled' : 'Disabled'}
                      tone={flag.enabled ? 'success' : 'neutral'}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell>
                    <StatusPill
                      label={flag.public ? 'Public' : 'Internal'}
                      tone={flag.public ? 'info' : 'neutral'}
                      size="sm"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="sm"
                      variant={flag.enabled ? 'secondary' : 'primary'}
                      loading={pendingName === flag.name}
                      disabled={pendingName !== null && pendingName !== flag.name}
                      onClick={() => handleToggle(flag)}
                    >
                      {flag.enabled ? 'Disable' : 'Enable'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </DataTableBody>
          </Table>
        </TableContainer>
      </Card>

      <Snackbar {...snackbar.snackbarProps} />
    </Stack>
  );
}
